/**
 * How an account is named on screen.
 *
 * An imported account has two labels and they are not interchangeable:
 *
 *   name          what the institution calls it -- "Non-registered",
 *                 "Individual Margin", "Invest for Business". Restated by
 *                 every sync, and not necessarily unique: a household can
 *                 genuinely hold three accounts all called "Non-registered".
 *   display_name  what the USER decided to call it. Written only by an
 *                 explicit edit, never by an import.
 *
 * Everything on screen renders `accountLabel`. Anywhere a renamed account
 * needs to stay traceable back to the real one -- and that is anywhere the
 * user might be choosing WHICH account to act on -- also renders
 * `accountSourceName`, which is non-empty precisely when a rename is hiding
 * something.
 */

/** The subset of an account these helpers need. */
export interface NamedAccount {
  name: string;
  display_name?: string | null;
}

/**
 * The name to show.
 *
 * @param account Any object carrying `name` and optionally `display_name`.
 * @returns The user's label when set, otherwise the source's own name.
 */
export function accountLabel(account: NamedAccount): string {
  const chosen = (account.display_name ?? '').trim();
  return chosen || account.name;
}

/**
 * The original name, but ONLY when a rename is hiding it.
 *
 * Returns an empty string when the account is not renamed, so a caller can
 * write `{source && <span>{source}</span>}` and never render "Chequing" twice.
 *
 * @param account Any object carrying `name` and optionally `display_name`.
 * @returns The source's name when it differs from what is displayed, else ''.
 */
export function accountSourceName(account: NamedAccount): string {
  const chosen = (account.display_name ?? '').trim();
  if (!chosen) return '';
  return chosen === account.name ? '' : account.name;
}

/**
 * Whether an account has been relabelled by the user.
 *
 * @param account Any object carrying `name` and optionally `display_name`.
 * @returns True when a user label is set and actually differs.
 */
export function isRenamed(account: NamedAccount): boolean {
  return accountSourceName(account) !== '';
}
